// useGanttLogic.js - Enhanced with ghost task support
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getAllTasksWithGhosts } from '../utils/ganttUtils';
import {
  ROW_NAME_WIDTH,
  EXPANSION_THRESHOLD,
  EXPANSION_AMOUNT,
  DAY_WIDTHS,
  VIEW_MODES,
  DEFAULT_TIMELINE_START,
  DEFAULT_TIMELINE_END,
  DEFAULT_MAX_GHOST_INSTANCES,
} from '../constants/ganttConstants';

export const useGanttLogic = ({
  categories = [],
  baseDate = new Date(),
  onTaskClick = null,
  onDateRangeChange = null,
  showGhostTasks = true,
  maxGhostInstances = DEFAULT_MAX_GHOST_INSTANCES,
  closingDaysMap = null,
  processCategories = null,
}) => {
  const scrollContainerRef = useRef(null);
  const [scrollState, setScrollState] = useState({
    scrollLeft: 0,
    timelineStart: DEFAULT_TIMELINE_START,
    timelineEnd: DEFAULT_TIMELINE_END,
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [viewMode, setViewMode] = useState(VIEW_MODES.MONTH);
  const [pendingViewMode, setPendingViewMode] = useState(null);
  const currentDayIndexRef = useRef(0);

  // Get day width based on view mode
  const DAY_WIDTH = useMemo(() => {
    return DAY_WIDTHS[viewMode] || DAY_WIDTHS[VIEW_MODES.MONTH];
  }, [viewMode]);

  // Calculate current view date range for ghost task generation
  const currentViewDateRange = useMemo(() => {
    const startDate = new Date(baseDate);
    startDate.setDate(startDate.getDate() + scrollState.timelineStart);
    
    const endDate = new Date(baseDate);
    endDate.setDate(endDate.getDate() + scrollState.timelineEnd);
    
    return { startDate, endDate };
  }, [baseDate, scrollState.timelineStart, scrollState.timelineEnd]);

  // Process categories — custom processor takes priority, otherwise use ghost task logic
  const ganttCategories = useMemo(() => {
    if (categories.length === 0) return [];

    if (processCategories) {
      return processCategories(categories, currentViewDateRange);
    }

    if (!showGhostTasks) {
      return categories;
    }

    return getAllTasksWithGhosts(
      categories,
      currentViewDateRange.startDate,
      currentViewDateRange.endDate,
      maxGhostInstances,
      closingDaysMap
    );
  }, [categories, processCategories, showGhostTasks, currentViewDateRange, maxGhostInstances, closingDaysMap]);

  // Convert scroll position to day index
  const scrollToDayIndex = useCallback((scrollLeft) => {
    return Math.floor(scrollLeft / DAY_WIDTH) + scrollState.timelineStart;
  }, [DAY_WIDTH, scrollState.timelineStart]);

  // Convert day index to scroll position
  const dayIndexToScroll = useCallback((dayIndex) => {
    return (dayIndex - scrollState.timelineStart) * DAY_WIDTH;
  }, [DAY_WIDTH, scrollState.timelineStart]);

  // Initialize scroll position to center on base date
  useEffect(() => {
    if (!isInitialized && scrollContainerRef.current) {
      const initialScrollPosition = dayIndexToScroll(0) - (scrollContainerRef.current.clientWidth * 0.3);
      scrollContainerRef.current.scrollLeft = Math.max(0, initialScrollPosition);
      setScrollState(prev => ({
        ...prev,
        scrollLeft: Math.max(0, initialScrollPosition)
      }));
      setIsInitialized(true);
    }
  }, [isInitialized, dayIndexToScroll]);

  // Handle scroll with timeline expansion and data fetching
  const handleScroll = useCallback((e) => {
    const scrollLeft = e.target.scrollLeft;
    const scrollWidth = e.target.scrollWidth;
    const clientWidth = e.target.clientWidth;

    const scrollPercentage = scrollLeft / Math.max(scrollWidth - clientWidth, 1);

    setScrollState(prev => {
      let newState = { ...prev, scrollLeft };

      // Expand right side when very close to the end — fetch only new right delta
      if (scrollPercentage > EXPANSION_THRESHOLD) {
        const prevEnd = prev.timelineEnd;
        newState.timelineEnd = prevEnd + EXPANSION_AMOUNT;

        if (onDateRangeChange) {
          const fetchStart = new Date(baseDate);
          fetchStart.setDate(fetchStart.getDate() + prevEnd);
          const fetchEnd = new Date(baseDate);
          fetchEnd.setDate(fetchEnd.getDate() + newState.timelineEnd);
          onDateRangeChange(fetchStart, fetchEnd);
        }
      }

      // Expand left side when very close to the beginning — fetch only new left delta
      if (scrollPercentage < (1 - EXPANSION_THRESHOLD)) {
        const prevStart = prev.timelineStart;
        const currentAbsoluteDayIndex = Math.floor(scrollLeft / DAY_WIDTH) + prevStart;
        newState.timelineStart = prevStart - EXPANSION_AMOUNT;

        const newScrollPosition = (currentAbsoluteDayIndex - newState.timelineStart) * DAY_WIDTH;
        newState.scrollLeft = newScrollPosition;

        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft = newScrollPosition;
        }

        if (onDateRangeChange) {
          const fetchStart = new Date(baseDate);
          fetchStart.setDate(fetchStart.getDate() + newState.timelineStart);
          const fetchEnd = new Date(baseDate);
          fetchEnd.setDate(fetchEnd.getDate() + prevStart);
          onDateRangeChange(fetchStart, fetchEnd);
        }
      }

      return newState;
    });
  }, [DAY_WIDTH, baseDate, onDateRangeChange]);

  // Enhanced task click handler to differentiate between real and ghost tasks
  const handleTaskClick = useCallback((task) => {
    if (onTaskClick) {
      onTaskClick(task, {
        isGhost: task.isGhost || false,
        originalTaskId: task.originalTaskId || task.id,
        instanceNumber: task.instanceNumber || 0
      });
    }
  }, [onTaskClick]);

  // Navigation functions
  const jumpToToday = useCallback(() => {
    const today = new Date();
    const todayDayIndex = Math.floor((today - baseDate) / (1000 * 60 * 60 * 24));
    const targetScrollLeft = dayIndexToScroll(todayDayIndex) - (scrollContainerRef.current?.clientWidth * 0.3 || 0);
    
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        left: Math.max(0, targetScrollLeft),
        behavior: 'smooth'
      });
    }
  }, [baseDate, dayIndexToScroll]);

  const jumpToBaseDate = useCallback(() => {
    const targetScrollLeft = dayIndexToScroll(0) - (scrollContainerRef.current?.clientWidth * 0.3 || 0);
    
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        left: Math.max(0, targetScrollLeft),
        behavior: 'smooth'
      });
    }
  }, [dayIndexToScroll]);

  // Enhanced navigation to jump to next/previous recurring task instance
  const jumpToNextRecurringInstance = useCallback((originalTaskId) => {
    const now = new Date();
    let nextInstanceDate = null;
    let minDistance = Infinity;

    ganttCategories.forEach(category => {
      category.tasks.forEach(task => {
        if (task.isGhost && task.originalTaskId === originalTaskId && task.startDate > now) {
          const distance = task.startDate - now;
          if (distance < minDistance) {
            minDistance = distance;
            nextInstanceDate = task.startDate;
          }
        }
      });
    });

    if (nextInstanceDate) {
      const dayIndex = Math.floor((nextInstanceDate - baseDate) / (1000 * 60 * 60 * 24));
      const targetScrollLeft = dayIndexToScroll(dayIndex) - (scrollContainerRef.current?.clientWidth * 0.3 || 0);
      
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          left: Math.max(0, targetScrollLeft),
          behavior: 'smooth'
        });
      }
    }
  }, [ganttCategories, baseDate, dayIndexToScroll]);

  const totalDays = scrollState.timelineEnd - scrollState.timelineStart;

  // Handle view mode changes while preserving the current day in view
  const handleViewModeChange = useCallback((newViewMode) => {
    if (scrollContainerRef.current) {
      // Calculate the current center day index before changing view mode
      const currentScrollLeft = scrollContainerRef.current.scrollLeft;
      const viewportCenter = currentScrollLeft + (scrollContainerRef.current.clientWidth / 2);
      currentDayIndexRef.current = scrollToDayIndex(viewportCenter);
    }
    setPendingViewMode(newViewMode);
    setViewMode(newViewMode);
  }, [scrollToDayIndex]);

  // Adjust scroll position when view mode changes
  useEffect(() => {
    if (pendingViewMode && scrollContainerRef.current) {
      // Scroll to the same day index that was centered before the view change
      const targetScrollLeft = dayIndexToScroll(currentDayIndexRef.current) - (scrollContainerRef.current.clientWidth / 2);
      scrollContainerRef.current.scrollLeft = Math.max(0, targetScrollLeft);
      setScrollState(prev => ({
        ...prev,
        scrollLeft: Math.max(0, targetScrollLeft)
      }));
      setPendingViewMode(null);
    }
  }, [pendingViewMode, dayIndexToScroll]);

  // Calculate statistics including ghost tasks
  const taskStats = useMemo(() => {
    const stats = {
      totalTasks: 0,
      recurringTasks: 0,
      ghostTasks: 0,
      categoriesWithRecurring: 0
    };

    ganttCategories.forEach(category => {
      let categoryHasRecurring = false;
      category.tasks.forEach(task => {
        stats.totalTasks++;
        if (task.isGhost) {
          stats.ghostTasks++;
        } else if (task.is_recurring) {
          stats.recurringTasks++;
          categoryHasRecurring = true;
        }
      });
      if (categoryHasRecurring) {
        stats.categoriesWithRecurring++;
      }
    });

    return stats;
  }, [ganttCategories]);

  return {
    scrollContainerRef,
    scrollState,
    isInitialized,
    baseDate,
    categories: ganttCategories,
    taskStats,
    currentViewDateRange,
    handleScroll,
    scrollToDayIndex,
    jumpToToday,
    jumpToBaseDate,
    jumpToNextRecurringInstance,
    totalDays,
    DAY_WIDTH,
    ROW_NAME_WIDTH,
    onTaskClick: handleTaskClick,
    viewMode,
    setViewMode: handleViewModeChange,
  };
};